import { useMemo, useState, type CSSProperties } from "react";
import {
  ALL_TILES,
  BOARD_RADIUS,
  buildBoard,
  deriveSlots,
  type Slot,
  type TileDef,
  type TileFace,
  type VigorZone,
} from "@fortress/shared";
import {
  RESOURCE_KINDS,
  RESOURCE_LABELS,
  ResourceIcon,
  resourceIconUrl,
} from "../resources";

// Tile geometry is derived, not stored: a tile's local node `i` lands on
// `slot.localPos[i]` (mapLocalToGlobal is the identity for face A / rotation 0).
// So one representative slot per zone gives the shape to lay every tile of that
// zone out on. Both faces reuse the same node positions, so they share a layout
// and only differ in which nodes carry which resource / severed mark.
const BOARD = buildBoard(BOARD_RADIUS);
const SLOTS = deriveSlots(BOARD);
const ZONE_SLOT = new Map<VigorZone, Slot>();
for (const s of SLOTS) if (!ZONE_SLOT.has(s.zone)) ZONE_SLOT.set(s.zone, s);

const ZONE_COLOR: Record<VigorZone, string> = {
  summit: "#cd6a4f",
  crag: "#dcdad0",
  ridgeline: "#d2b54e",
  foothill: "#a6c084",
  forest: "#77a98e",
};

interface Geom {
  pts: { x: number; y: number }[];
  /** Edges with both endpoints in the tile (local index pairs). */
  internal: [number, number][];
  /** Stubs to neighbours OUTSIDE the tile — the paths leaving it. `dir` is the
   * hex direction (0..5) identifying the edge for `disconnectedExits`. */
  leaving: { i: number; x2: number; y2: number; dir: number }[];
  w: number;
  h: number;
}

/** Lay a slot's local positions into an SVG-space shape (radial-out points up),
 * including short stubs for the paths that leave the tile to adjacent tiles. */
function slotGeom(slot: Slot, size = 210, pad = 30): Geom {
  const raw = slot.localPos.map((p) => ({ x: p.x, y: -p.y }));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of raw) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const bw = maxX - minX || 1;
  const bh = maxY - minY || 1;
  const scale = Math.min((size - 2 * pad) / bw, (size - 2 * pad) / bh);
  const pts = raw.map((p) => ({
    x: pad + (p.x - minX) * scale,
    y: pad + (p.y - minY) * scale,
  }));

  // Replicate the slot's own frame transform (centroid origin, +y radial out,
  // rotated by 90°−φ) so external neighbours land in the same orientation, then
  // flip y to match SVG — giving each leaving path's on-screen direction.
  const slotPix = slot.nodeIds.map((id) => BOARD.nodes[id]!.pixel);
  const cx = slotPix.reduce((s, p) => s + p.x, 0) / slotPix.length;
  const cy = slotPix.reduce((s, p) => s + p.y, 0) / slotPix.length;
  const phi = Math.atan2(cy, cx);
  const ca = Math.cos(Math.PI / 2 - phi);
  const sa = Math.sin(Math.PI / 2 - phi);

  const idx = new Map(slot.nodeIds.map((id, i) => [id, i]));
  const internal: [number, number][] = [];
  const leaving: { i: number; x2: number; y2: number; dir: number }[] = [];
  const stub = 18;
  for (let i = 0; i < slot.nodeIds.length; i++) {
    const ip = BOARD.nodes[slot.nodeIds[i]!]!.pixel;
    for (const nb of BOARD.adjacency[slot.nodeIds[i]!] ?? []) {
      const j = idx.get(nb);
      if (j !== undefined) {
        if (j > i) internal.push([i, j]);
        continue;
      }
      const np = BOARD.nodes[nb]!.pixel;
      const vx = np.x - ip.x;
      const vy = np.y - ip.y;
      const lx = vx * ca - vy * sa; // into the slot's local frame
      const ly = vx * sa + vy * ca;
      // Hex direction (0..5) of this leaving edge, in the canonical local frame.
      const dir = (((Math.round((Math.atan2(ly, lx) * 180) / Math.PI / 60) % 6) + 6) % 6);
      const dx = lx;
      const dy = -ly; // flip y for SVG
      const L = Math.hypot(dx, dy) || 1;
      leaving.push({
        i,
        x2: pts[i]!.x + (dx / L) * stub,
        y2: pts[i]!.y + (dy / L) * stub,
        dir,
      });
    }
  }
  return { pts, internal, leaving, w: bw * scale + 2 * pad, h: bh * scale + 2 * pad };
}

/** A printed-resource marker: the pre-rendered PNG art (client/public/
 * resources), with the permanent vs one-time look baked into the file. The PNGs
 * carry transparent padding, so the drawn box is scaled up to keep the glyph the
 * same visual size the old vector marker had (radius ~7). */
function ResourceMark({
  cx,
  cy,
  type,
  oneTime,
}: {
  cx: number;
  cy: number;
  type: string;
  oneTime: boolean;
}) {
  const half = 7 * 2.1; // box half-size compensating for the PNG's transparent margin
  return (
    <image
      href={resourceIconUrl(type, oneTime)}
      x={cx - half}
      y={cy - half}
      width={half * 2}
      height={half * 2}
    />
  );
}

/** A "T" barrier from node `p` toward `t` — short stem + perpendicular cap,
 * matching the main board's severed-path mark. */
function tBarrier(
  p: { x: number; y: number },
  t: { x: number; y: number },
  key: string,
) {
  const vx = t.x - p.x;
  const vy = t.y - p.y;
  const L = Math.hypot(vx, vy) || 1;
  const ux = vx / L;
  const uy = vy / L;
  const nx = -uy;
  const ny = ux;
  // Scale to the edge so two barriers (one per end) fit without overlapping.
  const cap = Math.min(13, L * 0.34); // crossbar distance from the node
  const s0 = Math.min(4, cap * 0.45); // stem starts just off the node
  const half = 3.2; // crossbar half-length
  const ex = p.x + ux * cap;
  const ey = p.y + uy * cap;
  return (
    <path
      key={key}
      d={`M ${p.x + ux * s0} ${p.y + uy * s0} L ${ex} ${ey} M ${ex + nx * half} ${ey + ny * half} L ${ex - nx * half} ${ey - ny * half}`}
      stroke="#2b2620"
      strokeWidth={1.6}
      strokeLinecap="round"
      fill="none"
    />
  );
}

const edgeKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

// --- Editable state -------------------------------------------------------

type Perm = "permanent" | "oneTime";
interface EditFace {
  res: Record<number, { type: string; permanence: Perm }>;
  severed: string[]; // internal edge keys "a-b"
  exits: string[]; // leaving edge keys "node:dir"
}
type EditTile = { A: EditFace; B: EditFace };

const exitKey = (i: number, dir: number) => `${i}:${dir}`;

function toEditFace(face: TileFace): EditFace {
  const res: EditFace["res"] = {};
  for (const r of face.resources) res[r.node] = { type: r.type, permanence: r.permanence };
  return {
    res,
    severed: face.disconnectedPaths.map(([a, b]) => edgeKey(a, b)),
    exits: (face.disconnectedExits ?? []).map(([i, d]) => exitKey(i, d)),
  };
}

const CYCLE: (string | undefined)[] = [undefined, "minerals", "food", "vigor", "obsidian"];

/** Left-click a node: advance its resource type (…→ none → mineral → …). */
function cycleType(face: EditFace, i: number): EditFace {
  const cur = face.res[i];
  const next = CYCLE[(CYCLE.indexOf(cur?.type) + 1) % CYCLE.length];
  const res = { ...face.res };
  if (!next) delete res[i];
  else res[i] = { type: next, permanence: cur?.permanence ?? "permanent" };
  return { ...face, res };
}
/** Right-click a node: toggle permanent ⇄ one-time. */
function togglePerm(face: EditFace, i: number): EditFace {
  const cur = face.res[i];
  if (!cur) return face;
  return {
    ...face,
    res: {
      ...face.res,
      [i]: { ...cur, permanence: cur.permanence === "oneTime" ? "permanent" : "oneTime" },
    },
  };
}
/** Click an internal edge: sever ⇄ restore that path. */
function toggleEdge(face: EditFace, key: string): EditFace {
  return {
    ...face,
    severed: face.severed.includes(key)
      ? face.severed.filter((k) => k !== key)
      : [...face.severed, key],
  };
}
/** Click a leaving stub: sever ⇄ restore that exit path. */
function toggleExit(face: EditFace, key: string): EditFace {
  return {
    ...face,
    exits: face.exits.includes(key)
      ? face.exits.filter((k) => k !== key)
      : [...face.exits, key],
  };
}

/** Emit the `faceA: {…}, faceB: {…}` literal to paste into tiles.ts. */
function tileTs(edit: EditTile): string {
  const faceTs = (f: EditFace) => {
    const paths =
      "[" +
      f.severed
        .map((k) => k.split("-"))
        .sort((a, b) => +a[0]! - +b[0]! || +a[1]! - +b[1]!)
        .map(([a, b]) => `[${a}, ${b}]`)
        .join(", ") +
      "]";
    const exitsArr = f.exits
      .map((k) => k.split(":").map(Number))
      .sort((a, b) => a[0]! - b[0]! || a[1]! - b[1]!)
      .map(([i, d]) => `[${i}, ${d}]`);
    const exitsLine = exitsArr.length
      ? `\n      disconnectedExits: [${exitsArr.join(", ")}],`
      : "";
    const ns = Object.keys(f.res).map(Number).sort((a, b) => a - b);
    const lines = ns
      .map((n) => `        res(${n}, "${f.res[n]!.type}", "${f.res[n]!.permanence}"),`)
      .join("\n");
    const resBlock = ns.length ? `[\n${lines}\n      ]` : "[]";
    return `{\n      disconnectedPaths: ${paths},${exitsLine}\n      resources: ${resBlock},\n    }`;
  };
  return `faceA: ${faceTs(edit.A)},\nfaceB: ${faceTs(edit.B)},`;
}

// --- Rendering ------------------------------------------------------------

function EditableFace({
  geom,
  edit,
  onNode,
  onNodeContext,
  onEdge,
  onExit,
}: {
  geom: Geom;
  edit: EditFace;
  onNode: (i: number) => void;
  onNodeContext: (i: number) => void;
  onEdge: (key: string) => void;
  onExit: (key: string) => void;
}) {
  const pts = geom.pts;
  const severed = new Set(edit.severed);
  const exits = new Set(edit.exits);
  return (
    <svg width={geom.w} height={geom.h} viewBox={`0 0 ${geom.w} ${geom.h}`}>
      {/* paths leaving the tile: click to sever/restore (T when severed) */}
      {geom.leaving.map((lv, k) => {
        const key = exitKey(lv.i, lv.dir);
        const p = pts[lv.i]!;
        return (
          <g key={`l${k}`}>
            {exits.has(key) ? (
              tBarrier(p, { x: lv.x2, y: lv.y2 }, `lt${k}`)
            ) : (
              <line x1={p.x} y1={p.y} x2={lv.x2} y2={lv.y2} stroke="#b3ab98" strokeWidth={1} />
            )}
            <line
              x1={p.x}
              y1={p.y}
              x2={lv.x2}
              y2={lv.y2}
              stroke="transparent"
              strokeWidth={10}
              style={{ cursor: "pointer" }}
              onClick={() => onExit(key)}
            />
          </g>
        );
      })}
      {/* internal edges: visible (line or "T") + a wide transparent hit line */}
      {geom.internal.map(([a, b], k) => {
        const key = edgeKey(a, b);
        return (
          <g key={`i${k}`}>
            {severed.has(key) ? (
              <>
                {tBarrier(pts[a]!, pts[b]!, `t${k}a`)}
                {tBarrier(pts[b]!, pts[a]!, `t${k}b`)}
              </>
            ) : (
              <line
                x1={pts[a]!.x}
                y1={pts[a]!.y}
                x2={pts[b]!.x}
                y2={pts[b]!.y}
                stroke="#b3ab98"
                strokeWidth={1}
              />
            )}
            <line
              x1={pts[a]!.x}
              y1={pts[a]!.y}
              x2={pts[b]!.x}
              y2={pts[b]!.y}
              stroke="transparent"
              strokeWidth={10}
              style={{ cursor: "pointer" }}
              onClick={() => onEdge(key)}
            />
          </g>
        );
      })}
      {/* nodes: icon/dot + index + a transparent hit circle on top */}
      {pts.map((p, i) => {
        const r = edit.res[i];
        return (
          <g key={i}>
            {r ? (
              <ResourceMark cx={p.x} cy={p.y} type={r.type} oneTime={r.permanence === "oneTime"} />
            ) : (
              <circle cx={p.x} cy={p.y} r={2.4} fill="#3a342c" />
            )}
            <text
              x={p.x + 6}
              y={p.y - 5}
              fontSize={8}
              fill="#9a9384"
              fontFamily="ui-monospace, monospace"
              style={{ pointerEvents: "none" }}
            >
              {i}
            </text>
            <circle
              cx={p.x}
              cy={p.y}
              r={9}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onClick={() => onNode(i)}
              onContextMenu={(e) => {
                e.preventDefault();
                onNodeContext(i);
              }}
            />
          </g>
        );
      })}
    </svg>
  );
}

function FacePanel({
  geom,
  edit,
  label,
  onNode,
  onNodeContext,
  onEdge,
  onExit,
}: {
  geom: Geom;
  edit: EditFace;
  label: string;
  onNode: (i: number) => void;
  onNodeContext: (i: number) => void;
  onEdge: (key: string) => void;
  onExit: (key: string) => void;
}) {
  const counts = RESOURCE_KINDS.map((t) => {
    const ns = Object.values(edit.res).filter((r) => r.type === t);
    return { t, n: ns.length, one: ns.filter((r) => r.permanence === "oneTime").length };
  }).filter((c) => c.n > 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{label}</div>
      <div style={{ background: "#fbf7ec", borderRadius: 8, border: "1px solid #e3dcc8" }}>
        <EditableFace
          geom={geom}
          edit={edit}
          onNode={onNode}
          onNodeContext={onNodeContext}
          onEdge={onEdge}
          onExit={onExit}
        />
      </div>
      <div style={{ fontSize: 11, color: "#6b6452", marginTop: 4, textAlign: "center" }}>
        {counts.length === 0 && <span>no resources</span>}
        {counts.map((c) => (
          <span
            key={c.t}
            style={{ display: "inline-flex", alignItems: "center", gap: 3, marginRight: 8 }}
          >
            <ResourceIcon kind={c.t} size={11} /> {RESOURCE_LABELS[c.t]} ×{c.n}
            {c.one > 0 ? ` (${c.one} one-time)` : ""}
          </span>
        ))}
        {edit.severed.length > 0 && (
          <div style={{ color: "#c0392b" }}>severed: {edit.severed.join(", ")}</div>
        )}
        {edit.exits.length > 0 && (
          <div style={{ color: "#c0392b" }}>exits: {edit.exits.join(", ")}</div>
        )}
      </div>
    </div>
  );
}

function TileCard({
  tile,
  geom,
  edit,
  onEdit,
  onReset,
}: {
  tile: TileDef;
  geom: Geom;
  edit: EditTile;
  onEdit: (faceKey: "A" | "B", fn: (f: EditFace) => EditFace) => void;
  onReset: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const ts = tileTs(edit);
  const copy = () => {
    void navigator.clipboard?.writeText(`// ${tile.id}\n${ts}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  const panelProps = (faceKey: "A" | "B") => ({
    onNode: (i: number) => onEdit(faceKey, (f) => cycleType(f, i)),
    onNodeContext: (i: number) => onEdit(faceKey, (f) => togglePerm(f, i)),
    onEdge: (key: string) => onEdit(faceKey, (f) => toggleEdge(f, key)),
    onExit: (key: string) => onEdit(faceKey, (f) => toggleExit(f, key)),
  });
  return (
    <div
      style={{
        border: "1px solid #ddd5c2",
        borderRadius: 12,
        padding: 16,
        background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 4,
            background: ZONE_COLOR[tile.zone],
            border: "1px solid rgba(0,0,0,0.2)",
          }}
        />
        <strong style={{ fontSize: 15 }}>{tile.id}</strong>
        <span style={{ fontSize: 12, color: "#888" }}>
          {tile.zone} · {tile.nodeCount} nodes
        </span>
        <button type="button" onClick={onReset} style={btnStyle}>
          reset
        </button>
        <button type="button" onClick={copy} style={{ ...btnStyle, fontWeight: 600 }}>
          {copied ? "copied!" : "copy TS"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 20, justifyContent: "center" }}>
        <FacePanel geom={geom} edit={edit.A} label="Face A" {...panelProps("A")} />
        <FacePanel geom={geom} edit={edit.B} label="Face B" {...panelProps("B")} />
      </div>
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: "pointer", fontSize: 12, color: "#888" }}>show TS</summary>
        <pre
          style={{
            fontSize: 11,
            background: "#f6f2e6",
            padding: 8,
            borderRadius: 6,
            overflowX: "auto",
            margin: "6px 0 0",
          }}
        >
          {ts}
        </pre>
      </details>
    </div>
  );
}

const btnStyle: CSSProperties = {
  marginLeft: 0,
  padding: "2px 10px",
  border: "1px solid #ccc3ac",
  borderRadius: 6,
  background: "#f6f2e6",
  color: "#2b2620",
  fontSize: 12,
  cursor: "pointer",
};

function Legend() {
  const sample = (oneTime: boolean) => (
    <svg width={28} height={20} viewBox="0 0 28 20">
      <ResourceMark cx={9} cy={10} type="minerals" oneTime={oneTime} />
    </svg>
  );
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 18,
        alignItems: "center",
        fontSize: 13,
        color: "#444",
        marginBottom: 18,
      }}
    >
      {RESOURCE_KINDS.map((t) => (
        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width={20} height={18} viewBox="0 0 20 18">
            <ResourceMark cx={10} cy={9} type={t} oneTime={false} />
          </svg>
          {RESOURCE_LABELS[t]}
        </span>
      ))}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {sample(false)} permanent
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {sample(true)} one-time (dashed)
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <svg width={18} height={16} viewBox="0 0 18 16">
          <path
            d="M 9 15 L 9 5 M 3.5 5 L 14.5 5"
            stroke="#2b2620"
            strokeWidth={1.6}
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        severed path (T barrier)
      </span>
    </div>
  );
}

export function TileLibrary() {
  const geomByZone = useMemo(() => {
    const m = new Map<VigorZone, Geom>();
    for (const [zone, slot] of ZONE_SLOT) m.set(zone, slotGeom(slot));
    return m;
  }, []);
  const [state, setState] = useState<Record<string, EditTile>>(() => {
    const s: Record<string, EditTile> = {};
    for (const t of ALL_TILES) s[t.id] = { A: toEditFace(t.faceA), B: toEditFace(t.faceB) };
    return s;
  });
  const editTile = (id: string, faceKey: "A" | "B", fn: (f: EditFace) => EditFace) =>
    setState((s) => ({ ...s, [id]: { ...s[id]!, [faceKey]: fn(s[id]![faceKey]) } }));
  const resetTile = (tile: TileDef) =>
    setState((s) => ({
      ...s,
      [tile.id]: { A: toEditFace(tile.faceA), B: toEditFace(tile.faceB) },
    }));

  return (
    <div
      style={{
        // The game sets `body { overflow: hidden }` for its full-screen Pixi
        // canvas, and #root is locked to height:100%. This page lives in that
        // same #root, so it must be its own scroll container or its tall grid
        // gets clipped (can't scroll). A fixed viewport height + overflowY:auto
        // does that without disturbing the game route.
        height: "100vh",
        overflowY: "auto",
        background: "#f2ecda",
        color: "#2b2620",
        padding: "24px 28px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>Tile Library</h1>
        <span style={{ color: "#777" }}>{ALL_TILES.length} tiles · editable</span>
        <a href="/" style={{ marginLeft: "auto", color: "#3f7fd0" }}>
          ← back to game
        </a>
      </div>
      <p style={{ color: "#555", marginTop: 0, fontSize: 13 }}>
        <strong>Edit:</strong> click a node to cycle its resource · right-click a node to
        toggle one-time · click any edge — internal <em>or</em> a stub leaving the tile —
        to sever/restore it · then <strong>copy TS</strong>{" "}
        and paste each face into <code>packages/shared/src/definitions/tiles.ts</code>.
        Edits are in-memory only.
      </p>
      <Legend />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(460px, 1fr))",
          gap: 18,
        }}
      >
        {ALL_TILES.map((tile) => {
          const geom = geomByZone.get(tile.zone);
          const edit = state[tile.id];
          if (!geom || !edit) return null;
          return (
            <TileCard
              key={tile.id}
              tile={tile}
              geom={geom}
              edit={edit}
              onEdit={(faceKey, fn) => editTile(tile.id, faceKey, fn)}
              onReset={() => resetTile(tile)}
            />
          );
        })}
      </div>
    </div>
  );
}
